import { ThemeEvents } from '@theme/events';

/**
 * Manages cart item selection and checkout permalink generation.
 */
class CartItemSelectionManager {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #refreshFrame = null;

  init() {
    this.refresh();

    document.addEventListener('click', this.#handleSelectionClick, true);
    document.addEventListener('change', this.#handleChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    if (document.body) {
      this.#observer = new MutationObserver(() => {
        this.scheduleRefresh();
      });

      this.#observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Schedules a refresh on the next animation frame.
   */
  scheduleRefresh() {
    if (this.#refreshFrame !== null) return;

    this.#refreshFrame = window.requestAnimationFrame(() => {
      this.#refreshFrame = null;
      this.refresh();
    });
  }

  /**
   * Refreshes checkbox state and checkout button availability.
   */
  refresh() {
    this.#syncCheckoutButtons();
  }

  /**
   * Handles checkbox changes.
   * @param {Event} event
   */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.name !== 'cart_item_select[]') return;

    event.stopPropagation();
    this.#syncCheckoutButtons();
  };

  /**
   * Prevents cart interactions from treating selection checkboxes like cart actions.
   * @param {MouseEvent} event
   */
  #handleSelectionClick = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.name !== 'cart_item_select[]') return;

    event.stopPropagation();
  };

  /**
   * Handles cart update events.
   * @param {Event} event
   */
  #handleCartUpdate = () => {
    this.scheduleRefresh();
  };

  /**
   * Returns all selection checkboxes.
   * @returns {HTMLInputElement[]}
   */
  #getCheckboxes() {
    return Array.from(document.querySelectorAll('input[name="cart_item_select[]"]'));
  }

  /**
   * Returns all checkout buttons controlled by selection state.
   * @returns {HTMLButtonElement[]}
   */
  #getCheckoutButtons() {
    return Array.from(document.querySelectorAll('[data-cart-item-selection-checkout]')).filter(
      (button) => button instanceof HTMLButtonElement
    );
  }

  /**
   * Gets selected cart rows.
   * @returns {HTMLElement[]}
   */
  #getSelectedRows() {
    const selectedRows = new Map();

    for (const checkbox of this.#getCheckboxes()) {
      if (!checkbox.checked) continue;

      const row = checkbox.closest('[data-cart-item-selection-row]');
      if (!(row instanceof HTMLElement)) continue;

      const lineItemKey = row.dataset.lineItemKey || checkbox.value;
      if (!lineItemKey || selectedRows.has(lineItemKey)) continue;

      selectedRows.set(lineItemKey, row);
    }

    return Array.from(selectedRows.values());
  }

  /**
   * Builds a cart permalink for the selected rows.
   * @param {HTMLElement[]} rows
   * @returns {string}
   */
  #buildCartPermalink(rows) {
    const variantQuantities = new Map();

    for (const row of rows) {
      const variantId = row.dataset.variantId;
      const quantityValue = Number(row.dataset.quantity || '0');

      if (!variantId || !Number.isFinite(quantityValue) || quantityValue <= 0) continue;

      const currentQuantity = variantQuantities.get(variantId) || 0;
      variantQuantities.set(variantId, currentQuantity + quantityValue);
    }

    const permalinkItems = Array.from(variantQuantities.entries())
      .map(([variantId, quantity]) => `${variantId}:${quantity}`)
      .join(',');

    if (!permalinkItems) return '';

    return new URL(`/cart/${permalinkItems}`, window.location.origin).toString();
  }

  /**
   * Updates checkout buttons based on selection state.
   */
  #syncCheckoutButtons() {
    const selectedRows = this.#getSelectedRows();
    const checkoutUrl = selectedRows.length > 0 ? this.#buildCartPermalink(selectedRows) : '';

    for (const button of this.#getCheckoutButtons()) {
      button.disabled = !checkoutUrl;
      button.dataset.checkoutUrl = checkoutUrl;
      button.setAttribute('aria-disabled', String(!checkoutUrl));
    }
  }
}

const selectionWindow = /** @type {Window & { __cartItemSelectionManager?: CartItemSelectionManager }} */ (window);

if (!selectionWindow.__cartItemSelectionManager) {
  selectionWindow.__cartItemSelectionManager = new CartItemSelectionManager();
  selectionWindow.__cartItemSelectionManager.init();
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-cart-item-selection-checkout]') : null;
  if (!(target instanceof HTMLButtonElement)) return;

  const checkoutUrl = target.dataset.checkoutUrl;
  if (!checkoutUrl) {
    event.preventDefault();
    return;
  }

  event.preventDefault();
  window.location.assign(checkoutUrl);
});
